export type HubAreaId = "graveyard" | "town" | "forest" | "city";

export type RoadAreaId =
  | "road_graveyard_town"
  | "road_town_forest"
  | "road_forest_city";

export type AreaId = HubAreaId | RoadAreaId;

export type LocationType =
  | "tavern"
  | "bank"
  | "shop"
  | "camp"
  | "hut"
  | "market"
  | "generic"
  | "smithy"
  | "workshop"
  | "tower";

export type SkillId =
  | "character"
  | "mining"
  | "woodcutting"
  | "fishing"
  | "fletching"
  | "thieving"
  | "magic"
  | "brewing"
  | "herblore"
  | "refining"
  | "smithing"
  | "cooking"
  | "clothing"
  | "enameling"
  | "inscribing"
  | "enchanting";

export type ClassId = "fighter" | "ranger" | "wizard" | "rogue" | "cleric";

export type SpellElement = "arcane" | "fire" | "frost" | "piercing" | "restoration";

export type SpellEffectType = "damage" | "heal" | "ward" | "shield" | "utility";

export interface Spell {
  id: string;
  key: string;
  name: string;
  tier: number;
  element: SpellElement;
  manaCost: number;
  effectType: SpellEffectType;
  description?: string | null;
}

export type DamageType = "fire" | "frost" | "arcane" | "piercing" | "melee" | "unarmed";

export interface Attributes {
  str: number;
  dex: number;
  con: number;
  int: number;
  wis: number;
  cha: number;
}

export interface SkillProgress {
  id: SkillId;
  level: number;
  xp: number;
}

export type ItemType =
  | "weapon"
  | "armor"
  | "consumable"
  | "resource"
  | "tool"
  | "currency"
  | "spell-scroll"
  | "ammo"
  | "jewelry"
  | "gem"
  | "bait"
  | "container";

export type ItemRarity = "common" | "uncommon" | "rare" | "epic";

export type ArmorCategory = "heavy" | "light" | "clothing" | "shield";

export type WeaponProficiency = "melee" | "ranged" | "magic";

export type EquipmentSlot =
  | "head"
  | "chest"
  | "legs"
  | "feet"
  | "hands"
  | "main_hand"
  | "off_hand"
  | "ring"
  | "neck"
  | "back";

export interface Item {
  id: string;
  key: string;
  name: string;
  type: ItemType;
  rarity: ItemRarity;
  basePrice: number;
  tags?: string[];
  description?: string;
  equipmentSlot?: EquipmentSlot;
  armorCategory?: ArmorCategory;
  armorTier?: number;
  carryCapacitySlotsMod?: number;
  maxManaMod?: number;
  manaRegenBonusPerTick?: number;
  // Combat stats (for weapons and armor)
  armorClass?: number;
  attackBonus?: number;
  damageDice?: string | null;
  damageBonus?: number;
  useEffects?: ItemEffect[];
  scrollSpellId?: string | null;
}

export type ItemEffectType =
  | "hp_restore"
  | "mana_restore"
  | "temp_hp"
  | "attr_mod"
  | "skill_mod"
  | "max_hp_debuff"
  | "max_mana_debuff";

export interface ItemEffect {
  id?: string;
  effectType: ItemEffectType;
  amount: number;
  targetKey?: keyof Attributes | SkillId | "hp" | "mana";
  durationHours?: number | null;
  maxPoints?: number | null;
}

export interface Enchantment {
  id: string;
  key?: string | null;
  name: string;
  description?: string | null;
  effectType: "attribute" | "skill" | "ward";
  target?: keyof Attributes | SkillId | "hp" | "mana";
  amount: number;
  wardType?: "hp" | "mana";
  levelReq?: number;
  xp?: number;
}

export interface InventoryEntry {
  itemId: string;
  variantKey?: string | null;
  quantity: number;
  item?: Item;
  instanceIds?: string[];
  enchantment?: Enchantment | null;
  isLegacy?: boolean;
  referencePrice?: ReferencePrice | null;
}

export interface LoadoutPresetSlot {
  slot: EquipmentSlot;
  itemId: string | null;
  itemInstanceId: string | null;
}

export interface LoadoutPreset {
  id: string;
  slotIndex: number;
  name: string;
  isActive: boolean;
  isEmpty?: boolean;
  slots: LoadoutPresetSlot[];
}

export interface LoadoutPresetsResponse {
  presets: LoadoutPreset[];
  activePresetId: string | null;
}

export interface GroundLootPile {
  id: string;
  itemId: string;
  itemName: string;
  qty: number;
  ownerId?: string | null;
  publicAt: string;
  expiresAt: string;
  item?: Item;
  referencePrice?: ReferencePrice | null;
}

export interface LootNotificationDrop {
  itemId: string;
  itemName: string;
  qty: number;
}

export interface LootNotification {
  npcName: string;
  npcStateId: string;
  drops: LootNotificationDrop[];
  message: string;
}

export type KilledByKind = "npc" | "player" | "unknown";

export interface DeathDropLine {
  itemId: string;
  itemName?: string;
  qty: number;
}

export interface DeathEvent {
  id: string;
  characterId: string;
  killedByKind: KilledByKind;
  killedByName?: string | null;
  deathAreaId: AreaId;
  deathAreaName: string;
  deathLocationId?: string | null;
  deathLocationName?: string | null;
  diedAt: string;
  publicAt: string;
  expiresAt: string;
  processedAt?: string | null;
  acknowledgedAt?: string | null;
  drops: DeathDropLine[];
}

export type RandomEncounterContextType = "travel" | "gathering" | "processing";

export type RandomEventOutcome =
  | { type: "loot_found"; message: string; drops: Array<{ itemId: string; qty: number }> }
  | { type: "gold_drop"; message: string; goldLost: number }
  | { type: "restore"; message: string; hpRestored: number; manaRestored: number }
  | { type: "combat_xp"; message: string; xpGranted: number }
  | { type: "attr_mod"; message: string; changes: Array<{ attr: keyof Attributes; delta: number; durationHours: number }> }
  | { type: "skill_mod"; message: string; changes: Array<{ skillId: SkillId; delta: number; durationHours: number }> };

export interface RandomEventResults {
  id: string;
  contextType: RandomEncounterContextType;
  triggeredAt: string;
  activityContext: string;
  outcomes: RandomEventOutcome[];
}

export interface CombatStats {
  armorClass: number;
  attackBonus: number;
  damageDice: string;
  damageBonus: number;
}

export interface Character {
  id: string;
  playerId: string;
  name: string;
  classId: ClassId;
  attributes: Attributes;
  hp: number;
  mana: number;
  max_hp: number;
  max_mana: number;
  temp_hp?: number;
  temp_hp_expires_at?: string | null;
  max_hp_debuff?: number;
  max_hp_debuff_expires_at?: string | null;
  max_mana_debuff?: number;
  max_mana_debuff_expires_at?: string | null;
  areaId: AreaId;
  locationId?: string | null;
  skillProgress: SkillProgress[];
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  gold: number;
  armorProficiencies: ArmorCategory[];
  weaponProficiencies: WeaponProficiency[];
  combatStats?: CombatStats;
  pendingLevelups?: number;
  apAvailable?: number;
}

export interface CarryState {
  carryCapacitySlots: number;
  slotsUsed: number;
  overCapacity: number;
  encumbered: boolean;
  encumbranceDexPenalty: number;
  effectiveDexForEncumbrance: number;
  heavyEncumbered: boolean;
  travelTimeMultiplier: number;
}

export interface Player {
  id: string;
  email: string;
  createdAt: string;
}

export interface Area {
  id: AreaId;
  name: string;
  description: string;
}

export interface Location {
  id: string;
  areaId: AreaId;
  name: string;
  type: LocationType | string;
  description?: string;
  services?: string[];
}

export type NpcDisposition = "friendly" | "neutral" | "hostile";

export type InteractionOption =
  | "talk"
  | "trade"
  | "fight"
  | "steal"
  | "influence"
  | "inspect";

export interface Npc {
  /** Unique instance id (npc_state.id) */
  id: string;
  /** Template id from npcs table */
  templateId: string;
  name: string;
  areaId: AreaId;
  locationId?: string | null;
  disposition: NpcDisposition;
  role: "vendor" | "quest-giver" | "trainer" | "wanderer" | "guard";
  hasShop: boolean;
  combatStyle?: "melee" | "ranged" | "magic";
}

export type ResourceType = "tree" | "rock" | "water" | "plant";

export interface ResourceNode {
  id: string;
  areaId: AreaId;
  locationId?: string | null;
  name: string;
  resourceType: ResourceType;
  skillId: SkillId;
  levelReq: number;
  baseIntervalMs: number;
  yieldsItemId: string;
  yieldsItemName?: string;
  xpPerGather: number;
  requiredItemId?: string | null;
  requiredItemName?: string | null;
  /**
   * How the required item is checked.
   * - "inventory": must exist in inventory (consumed per tick for things like bait)
   * - "equipped": must be equipped (for equippable tools)
   */
  requiredItemMode?: "inventory" | "equipped" | null;
}

export interface ResourceNodeState {
  nodeId: string;
  currentPool: number;
  depleted: boolean;
  respawnsAt?: string | null;
}

export interface Encounter {
  id: string;
  type: "combat" | "conversation" | "trade";
  areaId: AreaId;
  participants: EncounterParticipant[];
  turnOrder: string[];
  activeTurn?: string;
  turnStartedAt?: string;
  turnNumber?: number;
  /** Tracks bonus actions (e.g., consumable use) per participant keyed by participant id -> turnStartedAt ISO string. */
  bonusActions?: Record<string, string>;
  log?: string[];
  createdAt: string;
}

export interface EncounterParticipant {
  id: string;
  kind: "player" | "npc";
  name: string;
  hp: number;
  combatStyle?: "melee" | "ranged" | "magic" | "unarmed";
  maxHp?: number;
  tempHp?: number;
  tempHpExpiresAt?: string | null;
  mana?: number;
  maxMana?: number;
  maxHpDebuff?: number;
  maxManaDebuff?: number;
  lastRegenTurn?: number;
  activeWard?: { type: DamageType; expiresAtTurn: number } | null;
  initiative: number;
  /** For NPCs, track the specific instance state id */
  npcStateId?: string;
  /** For NPCs, track the template id */
  templateId?: string;
  /** Track if participant is in defensive stance (+2 AC until next turn) */
  isDefending?: boolean;
}

export type ActionStatus = "active" | "paused" | "stopped";

export interface ActionState {
  id: string;
  characterId: string;
  type: "move" | "gather" | "craft" | "combat" | "processing" | "casting";
  startedAt: string;
  completesAt: string;
  status?: ActionStatus;
  payload?: Record<string, unknown>;
}

export interface GatheringAction extends ActionState {
  type: "gather";
  nodeId: string;
  skillId: SkillId;
  intervalMs: number;
  lastResolvedAt: string;
}

export interface RecipeInput {
  itemId: string;
  quantity: number;
  itemName?: string;
  itemType?: Item["type"];
  itemRarity?: Item["rarity"];
  itemBasePrice?: number;
  itemDescription?: string;
}

export interface RecipeOutput {
  itemId: string;
  quantity: number;
  itemName?: string;
  itemType?: Item["type"];
  itemRarity?: Item["rarity"];
  itemBasePrice?: number;
  itemDescription?: string;
}

export interface Recipe {
  id: string;
  recipeKey: string;
  name: string;
  skillId: SkillId;
  levelReq: number;
  durationMs: number;
  xp: number;
  requiredToolItemId?: string | null;
  requiredToolName?: string | null;
  requiredLocationId?: string | null;
  requiredLocationName?: string | null;
  inputs: RecipeInput[];
  outputs: RecipeOutput[];
}

export interface ProcessingAction extends ActionState {
  type: "processing";
  recipeId: string;
  recipeName: string;
  skillId: SkillId;
  durationMs: number;
  lastResolvedAt: string;
  remaining: number;
  total: number;
  progressMs?: number;
  isPaused?: boolean;
}

export type ProcessingActionResponse = ProcessingAction;

export interface CastingAction extends ActionState {
  type: "casting";
  spellId: string;
  spellName: string;
  tier: number;
  durationMs: number;
  lastResolvedAt: string;
  isPaused?: boolean;
  progressMs?: number;
  /** For spell-specific actions (Superheat/Boil Water). */
  recipeId?: string | null;
}

export interface ApiError {
  message: string;
  code?: string;
}

export type ApiResponse<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest extends LoginRequest {}

export interface CharacterCreateRequest {
  name: string;
  classId: ClassId;
  attributes: Attributes;
  armorProficiencies?: ArmorCategory[];
  weaponProficiencies?: WeaponProficiency[];
}

export interface MoveRequest {
  to: HubAreaId;
  clientRequestId: string;
}

export interface TravelState {
  fromAreaId: HubAreaId;
  toAreaId: HubAreaId;
  roadAreaId: RoadAreaId;
  arrivalAt: string;
  isPaused: boolean;
  /** Frozen while paused; counts down while walking. */
  remainingMs: number;
}

export interface TravelOption {
  toAreaId: HubAreaId;
  durationMs: number;
}

export interface EnterLocationRequest {
  locationId: string;
}

export interface ExitLocationRequest {}

export interface StartCombatRequest {
  /** Target a specific NPC instance (npc_state.id) */
  npcStateId: string;
  clientRequestId: string;
}

export type TurnAction = "attack" | "defend" | "flee" | "reload" | "hot_swap" | "cast";

export interface TurnRequest {
  encounterId: string;
  action: TurnAction;
  targetId?: string;
  spellId?: string;
  /** For reload action: which arrow item to load (stackable item id). */
  reloadItemId?: string;
  /** For hot_swap action: which quiver instance to equip. */
  swapContainerInstanceId?: string;
  /** Optional quantity hint for reload (defaults to as many as will fit). */
  quantity?: number;
  clientRequestId: string;
}

export interface EncounterTurnResponse {
  encounter: Encounter;
  lootNotifications?: LootNotification[];
}

export interface EncounterPollResponse {
  encounter: Encounter;
  lootNotifications?: LootNotification[];
}

export interface ActiveEffectEntry {
  kind: "attr" | "skill";
  key: keyof Attributes | SkillId;
  /** Positive values are buffs; negative values are debuffs. */
  magnitude: number;
  /** Earliest decay timestamp among the stacked points of this sign. */
  nextDecayAt: string;
}

export interface AreaRuntimeState {
  areaId: string;
  npcs: Record<string, { respawnsAtMs: number }>;
  nodes: Record<string, { depletedAtIso: string; respawnsAtIso: string }>;
}

export interface AreaViewResponse {
  area?: Area;
  areaState?: AreaRuntimeState;
  locations?: Location[];
  currentLocationId?: string | null;
  npcs?: Npc[];
  players?: { id: string; name: string; level?: number }[];
  resourceNodes?: ResourceNode[];
  actions?: unknown[];
  currentGatheringAction?: GatheringAction | null;
  currentProcessingAction?: ProcessingAction | null;
  currentCastingAction?: CastingAction | null;
  processingRecipes?: Recipe[];
  inventory?: InventoryEntry[];
  groundLoot?: GroundLootPile[];
  gold?: number;
  equipment?: Record<EquipmentSlot, InventoryEntry | null>;
  carry?: CarryState | null;
  hp?: number;
  mana?: number;
  activeEncounterId?: string | null;
  deathEvent?: DeathEvent | null;
  randomEvent?: RandomEventResults | null;
  effectSums?: {
    attr: Partial<Record<keyof Attributes, number>>;
    skill: Partial<Record<SkillId, number>>;
  } | null;
  enchantments?: Enchantment[];
  travel?: TravelState | null;
  travelOptions?: TravelOption[];
  skillProgress?: SkillProgress[];
  pendingLevelups?: number;
  apAvailable?: number;
  activeEffects?: ActiveEffectEntry[] | null;
  tempHp?: number;
  tempHpExpiresAt?: string | null;
  maxHpDebuff?: number;
  maxHpDebuffExpiresAt?: string | null;
  maxManaDebuff?: number;
  maxManaDebuffExpiresAt?: string | null;
  spellbook?: {
    learned: Spell[];
    readied: Spell[];
    readyLimit: number;
  } | null;
}

export interface ReadySpellRequest {
  characterId: string;
  spellId: string;
  clientRequestId: string;
  position?: number;
}

export interface ReadySpellResponse {
  readied: Spell[];
  readyLimit: number;
}

export interface UnreadySpellRequest {
  characterId: string;
  spellId: string;
  clientRequestId: string;
}

export interface UnreadySpellResponse {
  readied: Spell[];
  readyLimit: number;
}

export interface MagicCastRequest {
  characterId: string;
  spellId: string;
  /** Optional: cast using a scroll item instance from inventory (consumed server-side). */
  fromScrollItemId?: string | null;
  clientRequestId: string;
  /** Optional recipe hint for Superheat Item. */
  recipeId?: string | null;
}

export interface MagicCastResponse {
  actionId: string;
  completesAt: string;
}

export interface MagicPollRequest {
  characterId: string;
  clientRequestId: string;
}

export interface MagicPollResponse {
  action: CastingAction | null;
  inventory: InventoryEntry[];
  stoppedReason?: string | null;
}

export interface MagicStopRequest {
  characterId: string;
}

export interface MagicStopResponse {
  stopped: boolean;
}

export interface MagicResumeRequest {
  characterId: string;
}

export interface MagicResumeResponse {
  resumed: boolean;
}

export interface TakeGroundLootRequest {
  characterId: string;
}

export interface UseItemRequest {
  itemId: string;
  clientRequestId: string;
}

export interface UseItemResponse {
  hp: number;
  mana: number;
  max_hp: number;
  max_mana: number;
  temp_hp: number;
  temp_hp_expires_at?: string | null;
  max_hp_debuff?: number;
  max_hp_debuff_expires_at?: string | null;
  max_mana_debuff?: number;
  max_mana_debuff_expires_at?: string | null;
  inventory: InventoryEntry[];
  activeEffects?: ActiveEffectEntry[];
  effectSums?: {
    attr: Partial<Record<keyof Attributes, number>>;
    skill: Partial<Record<SkillId, number>>;
  };
}

export interface TakeGroundLootResponse {
  inventory: InventoryEntry[];
  gold?: number;
}

export interface DropItemRequest {
  itemId: string;
  qty: number;
  clientRequestId: string;
}

export interface DropItemResponse {
  inventory: InventoryEntry[];
  groundLootPile: GroundLootPile;
  gold: number;
}

export interface BankState {
  bank: { gold: number; inventory: InventoryEntry[] };
  character: { gold: number; inventory: InventoryEntry[]; carry: CarryState };
}

export interface BankStateRequest {
  characterId: string;
}

export interface BankGoldRequest {
  characterId: string;
  amount: number;
  direction: "deposit" | "withdraw";
  clientRequestId: string;
}

export interface BankStackableRequest {
  characterId: string;
  itemId: string;
  quantity: number;
  direction: "deposit" | "withdraw";
  clientRequestId: string;
}

export interface BankInstanceRequest {
  characterId: string;
  instanceId: string;
  direction: "deposit" | "withdraw";
  clientRequestId: string;
}

export interface BankInstanceBulkRequest {
  characterId: string;
  instanceIds: string[];
  direction: "deposit" | "withdraw";
  clientRequestId: string;
}

export interface BankDepositAllRequest {
  characterId: string;
  clientRequestId: string;
}

export interface BankStateResponse extends BankState {}

export interface ContainerState {
  instanceId: string;
  itemId: string;
  itemKey: string;
  itemName?: string;
  kind: "backpack" | "quiver";
  capacitySlots?: number;
  maxQuantity?: number;
  carryBonusSlots?: number;
  contents: InventoryEntry[];
  slotsUsed: number;
  totalQuantity: number;
}

export interface ContainersResponse {
  containers: ContainerState[];
  inventory: InventoryEntry[];
  carry: CarryState;
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
}

export interface ContainerOpenRequest {
  characterId: string;
  emptyItemId: string;
  clientRequestId: string;
}

export interface ContainerTransferRequest {
  characterId: string;
  containerInstanceId: string;
  itemId: string;
  quantity: number;
  enchantmentId?: string | null;
  clientRequestId: string;
}

export interface ContainerPackAwayRequest {
  characterId: string;
  containerInstanceId: string;
  clientRequestId: string;
}

export interface AcknowledgeDeathEventRequest {
  clientRequestId: string;
}

export interface AcknowledgeDeathEventResponse {
  deathEvent?: DeathEvent | null;
}

export interface AcknowledgeRandomEventRequest {
  clientRequestId: string;
}

export interface AcknowledgeRandomEventResponse {
  randomEvent?: RandomEventResults | null;
}

export interface EquipRequest {
  itemId: string;
  instanceId?: string | null;
  slot: EquipmentSlot;
  clientRequestId: string;
}

export interface LoadoutPresetSaveRequest {
  clientRequestId: string;
}

export interface LoadoutPresetRenameRequest {
  name: string;
  clientRequestId: string;
}

export interface LoadoutPresetClearRequest {
  clientRequestId: string;
}

export interface LoadoutPresetApplyRequest {
  clientRequestId: string;
}

export interface EnchantRequest {
  instanceId: string;
  enchantmentId: string;
  clientRequestId: string;
}

export interface EnchantResponse {
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  mana?: number;
  enchantment?: Enchantment;
  levelUp?: { skillId: SkillId; newLevel: number };
  gainedXp?: number;
}

export interface DisenchantRequest {
  instanceId: string;
  clientRequestId: string;
}

export interface DisenchantResponse {
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  mana?: number;
  levelUp?: { skillId: SkillId; newLevel: number };
  gainedXp?: number;
}

export interface UnequipRequest {
  slot: EquipmentSlot;
  clientRequestId: string;
}

export interface LoadoutPresetSaveResponse {
  preset: LoadoutPreset;
  activePresetId: string | null;
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
}

export interface LoadoutPresetApplySummary {
  appliedSlots: number;
  missingSlots: number;
  blockedSlots: number;
  missing?: LoadoutPresetSlot[];
  blocked?: Array<{ slot: EquipmentSlot; reason: string }>;
}

export interface LoadoutPresetApplyResponse {
  preset: LoadoutPreset;
  activePresetId: string | null;
  summary: LoadoutPresetApplySummary;
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
}

export interface LoadoutPresetRenameResponse {
  preset: LoadoutPreset;
  activePresetId: string | null;
}

export interface LoadoutPresetClearResponse {
  preset: LoadoutPreset;
  activePresetId: string | null;
}

// Gathering action requests/responses
export interface ActionStartRequest {
  actionType: "gather";
  nodeId: string;
  clientRequestId: string;
}

export interface ProcessingStartRequest {
  actionType: "processing";
  recipeId: string;
  quantity: number;
  clientRequestId: string;
}

export interface ActionStartResponse {
  actionId: string;
  intervalMs: number;
  startedAt: string;
  nodeId: string;
  skillId: SkillId;
}

export interface ActionStopRequest {
  clientRequestId: string;
}

export interface ActionStopResponse {
  stopped: boolean;
}

export interface ActionPollRequest {
  clientRequestId: string;
}

export interface GatherResult {
  itemId: string;
  itemName: string;
  quantity: number;
}

export interface ActionPollResponse {
  results: GatherResult[];
  totalXp: number;
  skillId: SkillId;
  ticksResolved: number;
  failedTicks: number;
  nodeDepleted: boolean;
  actionComplete: boolean;
  currentPool?: number;
  interrupt?: { kind: "combat" | "event" } | null;
  wasteItemId?: string | null;
  wasteItemName?: string | null;
  inventory?: InventoryEntry[];
  gold?: number;
  levelUp?: { skillId: SkillId; newLevel: number };
}

export type OfflineSimulationSummary =
  | {
      kind: "gather";
      actionId: string;
      results: GatherResult[];
      totalXp: number;
      skillId: SkillId;
      ticksResolved: number;
      failedTicks: number;
      nodeDepleted: boolean;
      actionComplete: boolean;
      interrupt?: { kind: "combat" | "event" } | null;
      levelUp?: { skillId: SkillId; newLevel: number };
    }
  | {
      kind: "processing";
      actionId: string;
      outputs: RecipeOutput[];
      totalUnits: number;
      totalXp: number;
      skillId: SkillId;
      failedTicks: number;
      actionComplete: boolean;
      stoppedReason?: string | null;
      levelUp?: { skillId: SkillId; newLevel: number };
    }
  | {
      kind: "casting";
      actionId: string;
      spellId: string;
      spellName: string;
      completed: boolean;
      stoppedReason?: string | null;
      outputs?: RecipeOutput[];
      xpAwards?: Array<{ skillId: SkillId; xp: number; levelUp?: { skillId: SkillId; newLevel: number } }>;
    };

export interface OfflineSimulationRequest {
  clientRequestId: string;
}

export interface OfflineSimulationResponse {
  summaries: OfflineSimulationSummary[];
}

export interface ProcessingPollResult {
  outputs: RecipeOutput[];
  totalUnits: number;
  totalXp: number;
  remaining: number;
  actionComplete: boolean;
  failedTicks: number;
  status?: ActionStatus;
  progressMs?: number;
  stoppedReason?: string | null;
  wasteItemId?: string | null;
  wasteItemName?: string | null;
  inventory?: InventoryEntry[];
  gold?: number;
  levelUp?: { skillId: SkillId; newLevel: number };
}

export interface ShopPriceBreakdown {
  base: number;
  supplyMult: number;
  opinionMult: number;
  charismaMult: number;
  referencePrice?: ReferencePrice | null;
  undercut?: number | null;
}

export interface ShopStockEntry {
  id: string;
  itemId: string;
  itemName: string;
  itemType?: string;
  baselineQty: number;
  currentQty: number;
  price: number;
  priceBreakdown: ShopPriceBreakdown;
  isLegacy: boolean;
  referencePrice?: ReferencePrice | null;
}

export interface ShopInfo {
  id: string;
  name: string;
  description?: string | null;
  areaId: string;
  disposition: string;
}

export interface ShopOpinionSnapshot {
  npcOpinion: number;
  areaReputation: number;
  effectiveOpinion: number;
  bribeCost: number;
}

export interface ShopBrowseResponse {
  shop: ShopInfo;
  opinion: ShopOpinionSnapshot;
  stock: ShopStockEntry[];
}

export interface ShopTradeRequest {
  itemId: string;
  quantity: number;
}

export interface ShopTradeResponse {
  totalCost?: number;
  totalPayout?: number;
  pricePerUnit: number;
}

export interface ShopQuoteResponse {
  itemId: string;
  buyUnit: number;
  sellUnit: number;
  canBuy: boolean;
  breakdown: ShopPriceBreakdown;
}

export interface MarketOrderRequest {
  itemId: string;
  quantity: number;
  limitPrice: number;
  type: "buy" | "sell";
  clientRequestId: string;
  variantKey?: string | null;
}

export interface MarketOrderBookSideSummary {
  medianPrice: number;
  totalQty: number;
}

export interface MarketItemSummary {
  id: string;
  name: string;
  basePrice: number;
  type: string;
  equipmentSlot?: EquipmentSlot | null;
  tags?: string[];
}

export interface MarketItemListResponse {
  items: MarketItemSummary[];
}

export interface MarketOrderBook {
  buys: MarketOrderBookSideSummary;
  sells: MarketOrderBookSideSummary;
  basePrice: number;
  referencePrice?: ReferencePrice | null;
}

export type MarketOrderStatus = "open" | "filled" | "canceled";

export interface MarketOrderSummary {
  id: string;
  characterId: string;
  itemId: string;
  itemName?: string | null;
  variantKey?: string | null;
  type: "buy" | "sell";
  status: MarketOrderStatus;
  qtyRemaining: number;
  qtyFilled: number;
  qtyTotal: number;
  limitPrice: number;
  createdAt: string;
}

export interface MarketOrderListResponse {
  orders: MarketOrderSummary[];
  openCount: number;
}

export interface MarketInboxEntry {
  id: string;
  characterId: string;
  itemId?: string | null;
  itemName?: string | null;
  variantKey?: string | null;
  qty: number;
  gold: number;
  createdAt: string;
  claimedAt?: string | null;
}

export interface MarketInboxResponse {
  entries: MarketInboxEntry[];
}

export interface MarketHistoryPoint {
  bucket: string;
  median: number;
  count: number;
}

export interface MarketHistoryResponse {
  hourly: MarketHistoryPoint[];
  daily: MarketHistoryPoint[];
}

export interface SocialOpinionEntry {
  npcStateId: string;
  npcId: string;
  name: string;
  areaId: string;
  locationId: string | null;
  opinion: number;
  areaReputation: number;
  effectiveOpinion: number;
  bribeCost: number;
}

export interface SocialOpinionResponse {
  opinions: SocialOpinionEntry[];
}

export interface ReferencePrice {
  price: number;
  source: "fill" | "midpoint" | "base";
}

export interface SocialActionState {
  actionId: string;
  npcId: string;
  kind: "talk" | "bribe";
  startedAt: string;
  completesAt: string;
  remainingMs: number;
}

export interface SocialActionResult {
  npcId: string;
  opinionDelta: number;
  opinion: number;
  effectiveOpinion: number;
  bribeCost: number;
  xpGained: number;
  message: string;
}

export interface SocialActionStatusResponse {
  active: SocialActionState | null;
  result?: SocialActionResult | null;
}

// XP progression helpers (RuneScape-style exponential curve up to level 99)
export const XP_TABLE: number[] = (() => {
  const table: number[] = [0]; // level 1 requires 0 XP
  let points = 0;
  for (let level = 1; level < 99; level++) {
    points += Math.floor(level + 300 * Math.pow(2, level / 7));
    table[level] = Math.floor(points / 4);
  }
  return table;
})();

export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level >= 99) return XP_TABLE[98];
  return XP_TABLE[level - 1];
}

export function levelForXp(xp: number): number {
  if (xp <= 0) return 1;
  for (let level = 2; level <= 99; level++) {
    if (xp < XP_TABLE[level - 1]) {
      return level - 1;
    }
  }
  return 99;
}

// Combat XP progression: max level 20, same total XP as level 99 other skills
// This makes combat level much more slowly than other skills
export const COMBAT_XP_TABLE: number[] = (() => {
  const standardTable = XP_TABLE; // RuneScape-style 1-99 curve
  const table: number[] = [0]; // Level 1 = 0 XP

  // Generate XP requirements for combat levels 2-20
  // Map to standard skill levels 1-99 using exponential curve
  // Use a gentler curve (exponent 1.8) to better distribute early levels
  for (let combatLevel = 2; combatLevel <= 20; combatLevel++) {
    // ratio goes from 1/19 (for level 2) to 19/19 (for level 20)
    const ratio = (combatLevel - 1) / 19;
    // Map to standard levels with better distribution
    // Exponent 1.8 instead of 2.2 gives better early-game spread
    const standardLevel = Math.floor(1 + (97 * Math.pow(ratio, 1.8)));
    table[combatLevel - 1] = standardTable[Math.min(98, standardLevel)] || 0;
  }
  return table;
})();

export function combatXpForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level >= 20) return COMBAT_XP_TABLE[19];
  return COMBAT_XP_TABLE[level - 1];
}

export function combatLevelForXp(xp: number): number {
  if (xp <= 0) return 1;
  for (let level = 2; level <= 20; level++) {
    if (xp < COMBAT_XP_TABLE[level - 1]) {
      return level - 1;
    }
  }
  return 20;
}

/**
 * Calculate how many pending level-ups a character has based on their XP and current level
 */
export function calculatePendingLevelups(currentXp: number, currentLevel: number): number {
  const potentialLevel = combatLevelForXp(currentXp);
  return Math.max(0, potentialLevel - currentLevel);
}

export * from "./ws";
